import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem, GeoDataMap } from '@shared/abstraction';
import { chartGlobalData } from '@shared/constants/chart-global-data';
import { TaxResultItemType } from '@shared/enums/tax-result-item-type';
import { selectHandler } from '@shared/helpers/chart/select-handler';
import { unselectHandler } from '@shared/helpers/chart/unselect-handler';
import { TaxStatusThreshold } from '@shared/helpers/enums/tax-status-threshold';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { ChartServiceOptions } from '@shared/highcharts/char-service-options';
import { Client } from '@shared/models';
import { ChartConstructorType } from 'highcharts-angular';
import * as Highcharts from 'highcharts/highmaps';
import { cloneDeep } from 'lodash-es';
import { Observable, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { taxResultTypeItems } from 'src/app/modules/admin/constants/tax-result-type-items';
import { transactionTypeMapItems } from 'src/app/modules/admin/constants/transaction-type-map-items';
import { CountryService } from 'src/app/modules/admin/services';
import {
  GstarClient,
  TaxByJurisdictionRequest,
  TaxByJurisdictionResponse,
} from 'src/app/web-api-client';
import { CurrentUserService } from './current-user.service';
import { HeaderService } from './header.service';
@Injectable({
  providedIn: 'root',
})
export class ChartsService {
  private headerService = inject(HeaderService);
  private readonly gstarClient = inject(GstarClient);
  private translateService = inject(TranslateService);
  private readonly countryService = inject(CountryService);
  private readonly currentUserService = inject(CurrentUserService);

  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor: ChartConstructorType = 'mapChart';
  chartOptions: Highcharts.Options = {};

  localChartGlobalData: WritableSignal<GeoDataMap> = signal<GeoDataMap>(
    cloneDeep(chartGlobalData)
  );
  taxByJurisdictionData: WritableSignal<TaxByJurisdictionResponse[]> = signal<
    TaxByJurisdictionResponse[]
  >([]);
  showMap: WritableSignal<boolean> = signal<boolean>(false);

  transactionTypes = TransactionType;
  selectedTransactionType: WritableSignal<DropdownItem> = signal<DropdownItem>(
    transactionTypeMapItems[0]
  );
  taxResultTypes = TaxResultItemType;
  selectedTaxResultTypeType: WritableSignal<DropdownItem> =
    signal<DropdownItem>(taxResultTypeItems[0]);

  selectedCountryCode: WritableSignal<string | undefined> = signal(undefined);

  selectedCountryName$: Observable<string | undefined> = combineLatest([
    toObservable(this.selectedCountryCode),
    this.countryService.get(),
  ]).pipe(
    map(
      ([code, countries]) =>
        countries.find(country => country.code.toLowerCase() === code)?.name
    )
  );

  taxJurisdictionByFilters$: Observable<TaxByJurisdictionResponse[]> =
    combineLatest([
      toObservable(this.headerService.selectedClient),
      toObservable(this.selectedTransactionType),
      toObservable(this.selectedTaxResultTypeType),
    ]).pipe(
      tap(() => this.showMap.set(false)),
      switchMap(
        ([client, selectedTransactionType, selectedTaxResultTypeType]: [
          Client | undefined,
          DropdownItem,
          DropdownItem,
        ]) => {
          const req = new TaxByJurisdictionRequest({
            clientId: client?.id,
            taxResultItemTypeId: selectedTaxResultTypeType.value as number,
            transactionTypeId: selectedTransactionType.value as number,
          });
          if (
            !client ||
            !selectedTaxResultTypeType ||
            !selectedTransactionType ||
            !this.canViewTaxByJurisdiction()
          ) {
            this.taxByJurisdictionData.set([]);
            this.buildCharOptions();
            return of([]);
          }
          return this.gstarClient.datageochart(req).pipe(
            map((response: TaxByJurisdictionResponse[]) => {
              this.taxByJurisdictionData.set(response);
              this.buildCharOptions();
              return response;
            })
          );
        }
      ),
      tap(() => {
        this.showMap.set(true);
      })
    );

  /**
   * Tax by jurisdiction is tax result data, which the API refuses for a Country Admin.
   */
  private canViewTaxByJurisdiction(): boolean {
    return (
      this.currentUserService.isAdmin() ||
      !this.currentUserService.isCountryAdmin()
    );
  }

  public buildCharOptions(): void {
    this.resetSelectedCountry();
    this.updateChartData();
    this.chartOptions = {
      chart: {
        map: worldMap,
        spacingBottom: 40,
      },
      plotOptions: {
        series: {
          allowPointSelect: true,
          point: {
            events: {
              select: selectHandler,
              unselect: unselectHandler,
            },
          },
        },
      },
      title: {
        text: '',
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          alignTo: 'plotBox',
          align: 'right',
          verticalAlign: 'top',
        },
      },
      legend: {
        enabled: true,
        align: 'left',
        verticalAlign: 'top',
      },
      colorAxis: {
        layout: 'horizontal',
        dataClasses: [
          {
            name: this.translateService.instant(
              'dashboard.taxImpactMap.lowTaxColor'
            ),
            from: TaxStatusThreshold.LowLim,
            to: TaxStatusThreshold.LowLim,
            color: '#4EB523',
          },
          {
            name: this.translateService.instant(
              'dashboard.taxImpactMap.mediumTaxColor'
            ),
            from: TaxStatusThreshold.MediumLeftLim,
            to: TaxStatusThreshold.MediumRightLim,
            color: '#FFB600',
          },
          {
            name: this.translateService.instant(
              'dashboard.taxImpactMap.biggerTaxColor'
            ),
            from: TaxStatusThreshold.BiggerLeftLim,
            to: TaxStatusThreshold.BiggerRightLim,
            color: '#E86153',
          },
          {
            name: this.translateService.instant(
              'dashboard.taxImpactMap.noInvestmentColor'
            ),
            from: TaxStatusThreshold.NoInvestment,
            color: '#DEDEDE',
          },
        ],
      },
      series: [
        {
          type: 'map',
          name: this.translateService.instant('dashboard.taxImpactMap.label'),
          dataLabels: {
            enabled: false,
            format: '{point.name}',
          },
          tooltip: {
            pointFormatter: function (): string {
              return `<strong> ${this.name} </strong>: ${
                this.value === TaxStatusThreshold.NoInvestment
                  ? '-'
                  : (this.value as number).toFixed(this.value === 100 ? 0 : 4)
              }`;
            },
          },
          allAreas: false,
          data: this.localChartGlobalData(),
        },
      ],
    };

    (this.chartOptions.chart as ChartServiceOptions).selectedCountryCode =
      this.selectedCountryCode;
  }

  private updateChartData(): void {
    this.localChartGlobalData.set(cloneDeep(chartGlobalData));
    this.localChartGlobalData().forEach(dataPoint => {
      const matchingJuris = this.taxByJurisdictionData().find(
        (item: TaxByJurisdictionResponse) =>
          item.code === (dataPoint as [string, number | undefined])[0]
      );

      if (matchingJuris) {
        (dataPoint as [string, number | undefined])[1] =
          matchingJuris.value ?? 0;
      }
    });
  }

  private resetSelectedCountry = (): void =>
    this.selectedCountryCode.set(undefined);
}
